/*
 * File Created: Friday, 5th May 2023 6:05:44 pm
 * Author: Chenzier Kabrabam
 * Copyright (c) - 2018-2023 Addble Solutions Private Limited. All Rights Reserved.
 */

import React from "react";
import type { Item, Static } from "../types/static/StaticModal.type";
import { staticData } from "../data/staticData";
import SnackBar from "../components/SnackBar/SnackBar";
// import { Alert } from "@mui/material";
import { Colors } from "../constants/color";

export type CardContextType = {
  data: Static;
  addToCart: (id: string, qty?: string) => void;
  addAllItemsToCart: (items: Item[]) => void;
  removeItemFromCart: (id: string) => void;
  increaseQty: (id: string) => void;
  decreaseQty: (id: string) => void;
  getTotalItemAmount: () => number;
  items?: Item[];
};

export const CartContext = React.createContext({} as CardContextType);

function CartContextProvider({ children }: { children: React.ReactNode }) {
  const [storeData] = React.useState<Static>(staticData);
  const [items, setItems] = React.useState<Item[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [message , setMessage] = React.useState("");
  // const { decrement, increment, qty } = useQtyCount();

  // HANDLE SNACKBAR AUTOCLOSE
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setIsLoading(false);
    setMessage("");
  };

  // HANDLE ITEM ADD TO CART
  const addToCart = React.useCallback(
    (id: string, qty?: string) => {
      console.count("CARD CONTEXT : ADD TO CART : ");
      for (const products of staticData.sections) {
        const item = products.items.find((productId) => productId.id === id);
        if (item !== undefined) {
          // CHECKING IF ITEM IS ALREADY ADDED IN CART OR NOT
          const itemFound = items.find((oldProductID) => oldProductID.id === id);
          if (itemFound !== undefined) {
            console.log("CART CONTEXT : ADD TO CART : Item already Added");
            setMessage("Product is already in cart");
            setIsLoading(true);
            return null;
          }
          setItems((prev) => {
            if (qty !== undefined) {
              return [{ ...item, qty }, ...prev];
            }
            return [{ ...item, qty: "1" }, ...prev];
          });
          setMessage("Product added to cart");
          setIsLoading(true);
        }
      }
    },
    [items]
  );

  console.log("CART CONTEXT : ITEMS : ", items);
  
  // HANDLE ALL ITEMS ADD TO CART WHEN BUY ALL BUTTON CLICKED
  const addAllItemsToCart = React.useCallback(function addAllItemsToCart(allItems: Item[]) {

    // MODIFIED ITEMS
    const modifiedItems: Item[] = allItems.map((item) => {
      return { ...item, qty: "1" };
    });

    // setItems((prev) => [...modifiedItems, ...prev])
    setItems((prev) => {
      const updatedItems = [...prev];

      modifiedItems.forEach((newItem) => {
        const existingItemIndex = updatedItems.findIndex((item) => item.id === newItem.id);

        if (existingItemIndex !== -1) {
          setMessage("Product is already in cart");
          setIsLoading(true);
          updatedItems[existingItemIndex] = newItem;
        } else {
          setMessage("Product added to cart");
          setIsLoading(true);
          updatedItems.push(newItem)
        }
      })
      return updatedItems;
    })
    
  }, []);

  // HANDLE ITEM REMOVE FROM CART
  const removeItemFromCart = React.useCallback(
    function removeItemFromCart(id: string) {
      const filteredItem = items.filter((item) => item.id !== id);
      setMessage("Product removed from cart");
      setIsLoading(true);
      setItems(filteredItem);
    },
    [items]
  );

  // HANDLE ITEM QUANTITY INCREASE
  const handleIncrement = React.useCallback((id: string) => {
    console.log("CART CONTEXT : HANDLE INCREMENT : id ", id);
    setItems((prev) => {
      return prev.map((item) => {
        if (item.id === id && item.qty !== undefined) {
          return { ...item, qty: (parseFloat(item.qty) + 1).toString() };
        }
        return item;
      });
    });
  }, []);

  // HANDLE ITEM QUANTITY DECREASE
  const handleDecrement = React.useCallback((id: string) => {
    console.log("CART CONTEXT : HANDLE DECREMENT : id ", id);
    setItems((prev) => {
      return prev.map((item) => {
        if (item.id === id && item.qty !== undefined) {
          return { ...item, qty: item.qty !== "1" ? (parseFloat(item.qty) - 1).toString() : "1" };
        }
        return item;
      });
    });
  }, []);

  // HANDLE GET ITEMS TOTAL AMOUNT 
  const handleGetTotalItemAmount = React.useCallback(
    function getTotalItemAmount() {
      const amounts = items.map((item) => {
        const amount =
          item.qty !== undefined
            ? parseFloat((item.mrp * parseFloat(item.qty)).toString())
            : parseFloat(item.mrp.toString()) * 1;
        return amount;
      });
      console.log("CART CONTEXT : GET TOTAL ITEM AMOUNT : ", amounts);
      return amounts.reduce((prev, current) => prev + current, 0);
    },
    [items]
  );

  // function get(id: string, qty: string) {}
  /**
   * MEMOIZED CONTEXT VALUE
   */
  const contextValue: CardContextType = React.useMemo(
    (): CardContextType => ({
      data: storeData,
      addToCart,
      addAllItemsToCart,
      removeItemFromCart,
      decreaseQty: handleDecrement,
      increaseQty: handleIncrement,
      getTotalItemAmount: handleGetTotalItemAmount,
      items,
    }),
    [addAllItemsToCart, addToCart, handleDecrement, handleGetTotalItemAmount, handleIncrement, items, removeItemFromCart, storeData]
  );
  return (
    <CartContext.Provider value={contextValue}>
      {children}
      <SnackBar
        ContentProps={{
          sx: {
            backgroundColor: Colors.fallow,
            color: Colors.white,
          }
        }}
        open={isLoading}
        autoHideDuration={4000}
        onClose={handleClose}
        message={message}
      />
    </CartContext.Provider>);
}

export default React.memo(CartContextProvider);
